/** Case package export, stage authority and the package signing key (Phase 2 P2-3). */
export default {
  export: {
    title: 'Case package',
    open: 'Export package',
    intro:
      'A case package carries the whole history of this submission as one signed file that another system can check and import.',
    profile: 'What to include',
    profiles: {
      full: 'Full record',
      interagency: 'For another agency (no applicant contact details)',
      party: 'For the applicant',
      public: 'Public (no personal data)',
      archive: 'Archive (everything, with check reports and the procedure)',
    },
    boundary: 'Hand-off point',
    boundaries: {
      submission: 'Submission',
      supplement_withdraw: 'Supplement or withdrawal',
      referral: 'Referral to an agency',
      forward: 'Forward to another authority',
      decision: 'Decision',
      post_decision: 'After the decision',
      register: 'Register or statistics',
      snapshot: 'Current state (no hand-off)',
    },
    recipient: 'Receiving system',
    recipientHint: 'The id you use for the receiving system, in lower case, for example mbjb-osc.',
    recipientInvalid: 'Use lower-case letters, digits, dots, dashes or underscores, starting with a letter or a digit.',
    forwardWarning:
      'Forwarding hands this submission to the receiving system. Nothing can be changed here until an administrator recalls it.',
    start: 'Create package',
    status: {
      queued: 'Waiting to start…',
      running: 'Building the package…',
      done: 'The package is ready.',
      failed: 'The package could not be built.',
      expired: 'The package has expired and was deleted. Create it again.',
    },
    download: 'Download package',
    close: 'Close',
    asAt: 'History up to event {{seq}}',
    errors: {
      EXPORT_FAILED: 'Something went wrong while building the package. Try again.',
      EXPORT_CHAIN_BROKEN: 'The history of this submission failed verification, so it cannot be exported.',
      EXPORT_DOCUMENT_MISSING: 'A file of this submission is missing from storage.',
      EXPORT_DOCUMENT_MISMATCH: 'A stored file no longer matches the history.',
      EXPORT_REPORT_MISMATCH: 'A check report no longer matches the history.',
      EXPORT_PUBLIC_UNSAFE:
        'The history of this submission holds personal data in plain text, so no public package can be made.',
      EXPORT_TOO_LARGE: 'The package would be larger than 4 GB.',
      EXPORT_SEAL_KEY_UNAVAILABLE:
        'The package signing key cannot be used. An administrator must replace it in Settings → Planning.',
      EXPORT_GEOMETRY_FAILED: 'The site could not be read from the map register.',
      EXPORT_BOUNDARY_NOT_REACHED: 'The submission has not reached that hand-off point.',
      EXPORT_SEAL_KEY_REVOKED:
        'The key that signed this package was revoked. Create the package again to sign it with the new key.',
      EXPORT_STALE: 'Building the package stopped unexpectedly. Create it again.',
    },
  },
  authority: {
    heldTitle: 'Held by another system',
    heldBody: 'This submission was forwarded to {{holder}} on {{date}}. Actions here are refused until it is recalled.',
    heldBodyUnnamed:
      'This submission was forwarded to another system on {{date}}. Actions here are refused until it is recalled.',
    recall: 'Recall',
    recallTitle: 'Recall this submission',
    recallBody: 'Take the submission back from {{holder}}. Tell the receiving system that you have done so.',
    reasonLabel: 'Reason',
    reasonRequired: 'Enter a reason.',
    confirm: 'Recall submission',
    cancel: 'Cancel',
  },
  sealKey: {
    title: 'Package signing key',
    description:
      'Case packages are signed with this key. A receiving system checks the signature against the published key list.',
    active: 'Key in use: {{kid}}',
    created: 'Created {{date}}',
    none: 'No key yet. One is created with the first package.',
    retired: 'Earlier keys, still published so that earlier packages still verify',
    jwksUrl: 'Key list address',
    copy: 'Copy address',
    copied: 'Copied',
    download: 'Download key list',
    rotate: 'Replace key',
    rotateConfirm: 'Replace the signing key? New packages are signed with the new key; the old key stays published.',
    rotated: 'The signing key was replaced.',
    cancel: 'Cancel',
    revokePrevious: 'The old key may have leaked: revoke it, so that nothing it signed verifies any more',
    revokeConfirm:
      'Replace the signing key and revoke the old one? It is no longer published, so nothing it signed verifies any more, and every finished package it signed has to be created again.',
    revoked: 'Revoked keys, no longer published',
  },
  errors: {
    invalid_export: 'Those export options are not valid.',
    public_export_unsafe:
      'The history of this submission holds personal data in plain text, so no public package can be made.',
    boundary_not_reached: 'This submission has not reached that hand-off point. Export it as it stands now.',
    stage_held_externally: 'Another system holds this submission. An administrator must recall it first.',
    not_held_externally: 'No other system holds this submission.',
    intake_in_progress: 'The drawing check is still running. Forward the submission once it has finished.',
    export_not_found: 'That package was not found.',
    export_not_ready: 'That package is not ready yet.',
    export_expired: 'That package has expired and was deleted. Create it again.',
    too_many_exports: 'You already have three packages being built. Wait for one to finish.',
  },
  events: {
    case_authority_transferred: 'Forwarded to another system',
    case_authority_returned: 'Taken back from another system',
  },
} as const
